import Color = require("color-js");
import thunkMiddleware from "redux-thunk";
import { CoughMarshalClient } from "@vitalograph/coughmarshalclient";
import * as Actions from "./actions";
import rootReducer from "./CoreReducer";
import { toast } from "react-toastify";
import { Store, Dispatch } from "redux";
import { CoughAnalysisSetsFixedCommentsDisplay, CoughEventTypeEnum } from "../../misc/api/apiTypes";
import { timerMiddleware } from "../../misc/middleware/TimerMiddleware";
import Productionize from "../../misc/production-middleware";
import { mapToServerEvent } from "./eventMapping";
import { AnalyseState, EditModeEnum, LoadingStateEnum, PlayStateEnum, WaveformType } from "./interfaces/state";
import { PeaksJSChannel, PeaksPeriod, PeaksPoint } from "./interfacesPeaks/PeaksJS";
import { WebPortalLegacyClientInternal } from "../../misc/api/WebPortalTokenClient";
import { MapPeriod } from "./MappingPositionHelper";
import { WarningToast } from "./components/WarningToast";
import { StudyCoughEventType } from "webportalclient";

export interface ServerEvent {
    AnalysisSetId: string;
    EventTypeId: string;
    RawStart?: number;
    RawEnd?: number;
    ProcessedStart?: number;
    ProcessedEnd?: number;
    SensorProcessedStart?: number;
    SensorProcessedEnd?: number;
    vtaCt1Start?: number;
    vtaCt1End?: number;
    disputed?: boolean;
}

export enum AnalysisMode {
    Analysis = "Analysis",
    Readonly = "Readonly"
}

export const DEFAULT_WAVEFORM_TYPE = "Raw";

export const initialState = (primarySetId: string, mode?: boolean, requestedViewOnly?: boolean): AnalyseState => {
    const previousPlaybackTimepoint = localStorage.getItem(
        `analysisPreviousPlaybackTimepoint_${primarySetId}`
    );
    const timepointNumber =
        previousPlaybackTimepoint !== null ? parseFloat(previousPlaybackTimepoint) : null;

    const scrollAction = localStorage.getItem("analysisScrollAction") === "true";

    const analysisMode = mode ? AnalysisMode.Readonly : requestedViewOnly ? AnalysisMode.Readonly : AnalysisMode.Analysis;

    return {
        core: {
            areKeyboardShortcutsVisible: false,
            availableEventTypes: [],
            availableFixedComments: [],
            currentTimepoint: 0,
            isAddingSessionNote: false,
            isDownloadClicked: false,
            isEditMode: EditModeEnum.NotEditing,
            isEventListOpen: false,
            isDisputedListOpen: false,
            isSendForReviewModalOpen: false,
            isFixedCommentsDialogOpen: false,
            isGridlineVisible: true,
            isLoadError: false,
            configErrorMessage: null,
            isMicrophoneMuted: false,
            isScrollActionZoom: scrollAction,
            isSensorMuted: false,
            isSessionNotesVisible: false,
            canAddEventByDoubleClick: mode === false ? true : false,
            loadingState: LoadingStateEnum.Initial,
            isViewOnly: analysisMode === AnalysisMode.Readonly,
            mode: analysisMode,
            peaksJs: null,
            playState: PlayStateEnum.NotStarted,
            storedPlaybackTime: timepointNumber,
            previouslySelectedWaveform: null,
            selectedWaveform: DEFAULT_WAVEFORM_TYPE,
            sessionMetadata: null,
            sessionNotes: [],
            studyId: null,
            zoomLevel: 0,
            isHourSegmentsModalOpened: false,
            hourSegmentsMap: new Map(),
            isAcceptabilityAuditModalVisible: false,
            acceptabilityStatus: null,
            isSubmitting: false,
            rawTimestampPoints: null,
            previousPlaybackTimepoint: null,
            playbackServiceStatus: null,
            positions: null,
            coughAnalysisSet: null,
            comments: null,
            blindComments: null,
            requiresVisualReview: null,
            eventDuplicateThreshold: null,
            acceptanceErrorMessage: null,
            disputedAcceptanceErrorMessage: null,
            selectedSegment: null,
            isSegmented: false,
            visualInspectionErrorMessage: null,
            audioLength: null,
            waveformPoints: [],
            waveformPeriods: [],
            confirmedDuplicatePoints: new Set<string>(),
            adjudicationEnabled: false,
            isQCStatementsOpen: false,
        },
        analysisSet: {
            coughAnalysisSets: new Array<CoughAnalysisSetsFixedCommentsDisplay>(),
            isAnalysisSetDialogOpen: false,
            isLoaded: false,
            isMaximumAnalysisSetsAlertVisible: false,
            primaryAnalystSetId: primarySetId,
        }
    };
};

export default function configureStore(preloadedState: AnalyseState) {
    const createStoreWithMiddleware = Productionize([
        thunkMiddleware,
        peaksJSMiddleware,
        timerMiddleware
    ]);

    return createStoreWithMiddleware(rootReducer, preloadedState) as Store<AnalyseState>;
}

/**
 *
 * Verifying that the tag being added is within the bounds of the recording.
 * 
 * @param state
 * @param timePoint
 */
function VerifyTimeInsideWaveform(state: AnalyseState, timePoint: number): string {
    if ((timePoint === null || timePoint === undefined) || timePoint < 0) {
        return "Invalid Event. Events can't be added outside the cough file.";
    }

    const duration = state.core.peaksJs.player.getDuration();

    return timePoint > duration ? "Invalid Event. Events can't be added outside the cough file." : null;
}

function validateSegment(state: AnalyseState, segment: PeaksPeriod): string {
    return VerifyTimeInsideWaveform(state, segment.startTime) || VerifyTimeInsideWaveform(state, segment.endTime);
}

function validatePoint(state: AnalyseState, point: PeaksPoint): string {
    return VerifyTimeInsideWaveform(state, point.timestamp);
}

function validatePointTimestamps(state: AnalyseState, point: PeaksPoint | PeaksPeriod): string {

    if (point.isPoint) {
        return validatePoint(state, point as PeaksPoint);
    } else {
        return validateSegment(state, point as PeaksPeriod);
    }

}

function peaksJSMiddleware(store: Store<AnalyseState>) {
    return (next: any) => async (action: Actions.Action) => {
        const state = store.getState();

        switch (action.type) {
            case Actions.ActionType.InitializePeaksEvents:
                registerPeaksEvents(store.dispatch, state);
                break;
            case Actions.ActionType.DestoryPeaks:
                if (state.core.peaksJs) {
                    state.core.peaksJs.destroy();
                }
                break;
            case Actions.ActionType.Play:
                state.core.peaksJs.player.play();
                break;
            case Actions.ActionType.Pause:
                state.core.peaksJs.player.pause();
                break;
            case Actions.ActionType.Reset:
                state.core.peaksJs.player.seekBySeconds(0);
                break;
            case Actions.ActionType.JumpToEnd:
                jumpToEnd(state);
                break;
            case Actions.ActionType.ToggleMuteMicrophone:
                state.core.peaksJs.player.toggleChannelMute(PeaksJSChannel.Microphone);
                break;
            case Actions.ActionType.ToggleMuteSensor:
                state.core.peaksJs.player.toggleChannelMute(PeaksJSChannel.Sensor);
                break;
            case Actions.ActionType.ToggleGridline:
                state.core.peaksJs.waveform.toggleGridline();
                break;
            case Actions.ActionType.SetZoomLevel:
                state.core.peaksJs.zoom.setZoom(action.level);
                break;
            case Actions.ActionType.AddEvent:
                if (state.core.playState === PlayStateEnum.Playing) {
                    return;
                }

                const primarySetId = state.analysisSet.primaryAnalystSetId;
                const eventType = state.core.selectedEventType;

                const set = state.analysisSet.coughAnalysisSets.find(i => i.Id === primarySetId);

                let pointEvent: PeaksPoint;
                let periodEvent: PeaksPeriod;
                if (!eventType.period) {
                    pointEvent = {
                        ...action.event,
                        labelText: set.Type.Name,
                        isPoint: true,
                        color: Color(eventType.colour)
                            .setAlpha(0.6)
                            .toCSS(),
                        eventTypeId: eventType.id,
                        eventTypeName: eventType.name,
                        layerPosition: 0
                    } as PeaksPoint;
                }
                else {
                    periodEvent = {
                        id: action.event.id,
                        isPoint: false,
                        labelText: set.Type.Name,
                        editable: action.event.editable,
                        editMode: action.event.editMode,
                        color: Color(eventType.colour)
                            .setAlpha(0.6)
                            .toCSS(),
                        startTime: action.event.startTime !== undefined ? action.event.startTime : action.event.timestamp,
                        endTime: action.event.endTime !== undefined ? action.event.endTime : (action.event.timestamp !== undefined ? action.event.timestamp + 3 : action.event.startTime + 3),
                        eventTypeId: eventType.id,
                        eventTypeName: eventType.name,
                        layerPosition: 0
                    } as PeaksPeriod;
                }

                // If there is already a point of the SAME TYPE, then don't add it again and inform the user.
                if (
                    state.core.peaksJs.pointOrSegmentExists(
                        eventType.period ? periodEvent : pointEvent,
                        !eventType.period
                    )
                ) {
                    toast.error(
                        "A Marker Of Type " + eventType.name + " Already Exists At This Point."
                    );
                    return;
                }

                let err = validatePointTimestamps(state, eventType.period ? periodEvent : pointEvent);

                if (err) {
                    toast.error(
                        err
                    );
                    return;
                }

                addEventFetch(state, eventType.period ? periodEvent : pointEvent, store.dispatch, action.legacyClient);
                break;
            case Actions.ActionType.SkipBackward:
                const newTimeBackwards = state.core.peaksJs.time.getCurrentTime() - 3.5 * Math.pow(2, state.core.peaksJs.currentZoomLevel)
                state.core.peaksJs.time.setCurrentTime(newTimeBackwards < 0 ? 0 : newTimeBackwards);
                break;
            case Actions.ActionType.SkipForward:
                const newTimeForwards = state.core.peaksJs.time.getCurrentTime() + 3.5 * Math.pow(2, state.core.peaksJs.currentZoomLevel);
                state.core.peaksJs.time.setCurrentTime(newTimeForwards > state.core.audioLength ? state.core.audioLength - 0.001 : newTimeForwards);
                
                break;
            case Actions.ActionType.MovePointEvent:
                const moveablePointEvent = {
                    ...action.event,
                    editMode: true
                };

                state.core.peaksJs.points.edit(moveablePointEvent);
                break;
            case Actions.ActionType.MovePeriodEvent:
                const moveablePeriodEvent = {
                    ...action.event,
                    editMode: true,
                    editable: true
                };

                state.core.peaksJs.segments.edit(moveablePeriodEvent);
                break;
            case Actions.ActionType.FinishMovePointEvent:
                finishMovePointEvent(state, store.dispatch, action.legacyClient);
                break;
            case Actions.ActionType.FinishMovePeriodEvent:
                finishMovePeriodEvent(state, store.dispatch, action.coughMarshalClient, action.legacyClient);
                break;
            case Actions.ActionType.RemovePointEvent:
                let point: PeaksPoint;
                if (action.id) {
                    point = state.core.peaksJs.waveform.points.getPoints({
                        id: action.id
                    })[0];
                } else {
                    point = state.core.peaksJs.waveform.activePoint;
                }
                await removeEventFetch(state, point, store.dispatch, action.legacyClient);
                break;
            case Actions.ActionType.RemovePeriodEvent:
                let period: PeaksPeriod;
                if (action.id) {
                    period = state.core.peaksJs.waveform.segments.getSegments({
                        id: action.id
                    })[0];
                } else {
                    period = state.core.peaksJs.waveform.activeSegment;
                }
                await removeEventFetch(state, period, store.dispatch, action.legacyClient);
                break;
            case Actions.ActionType.ResumePlaybackJump:
                state.core.peaksJs.player.seekBySeconds(state.core.storedPlaybackTime - state.core.peaksJs.options.multiDayOffset);
                break;
            case Actions.ActionType.SavePlaybackPosition:
                localStorage.setItem(
                    `analysisPreviousPlaybackTimepoint_${state.analysisSet.primaryAnalystSetId}`,
                    (state.core.peaksJs.time.getCurrentTime() + state.core.peaksJs.options.multiDayOffset).toString()
                );
                break;
            case Actions.ActionType.ToggleScrollAction:
                localStorage.setItem(
                    "analysisScrollAction",
                    (!state.core.isScrollActionZoom).toString()
                );
                break;
            case Actions.ActionType.ChangeWaveform:
                action.legacyClient.changeWaveform(
                    state.core.sessionMetadata.sessionId,
                    state.analysisSet.primaryAnalystSetId,
                    state.core.isViewOnly,
                    action.waveformToChangeTo
                );
                break;
            case Actions.ActionType.JumpToTimepoint:
                state.core.peaksJs.player.seekBySeconds(action.timepoint);
                break;
            case Actions.ActionType.DeselectEditingEvents:
                const peaksJs = state.core.peaksJs;
                const segments = peaksJs.segments.getSegments({ editMode: true }) as PeaksPeriod[];
                const points = peaksJs.points.getPoints({ editMode: true }) as PeaksPoint[];

                segments.forEach(s => {
                    const newSegment = { ...s, editMode: false };
                    peaksJs.segments.remove(s);
                    peaksJs.segments.add(newSegment);
                });

                points.forEach(p => {
                    const newPoint = { ...p, editMode: false };
                    peaksJs.points.remove(p);
                    peaksJs.points.add(newPoint);
                });

                break;
            case Actions.ActionType.SetDuplicateVerified:
                const pointToUpdate = state.core.peaksJs.points.getPoints().find(x => x.id === action.id);
                state.core.peaksJs.points.remove(pointToUpdate);
                pointToUpdate.confirmedValidDuplicate = true;
                state.core.peaksJs.points.add(pointToUpdate);
                break;
            default:
        }
        return next(action);
    };
}

function registerPeaksEvents(dispatch: Dispatch<AnalyseState>, state) {
    state.core.peaksJs.on("player_ended", _data => {
        dispatch(Actions.PauseAsyncAction.create());
    });
}

function finishMovePointEvent(state: AnalyseState, dispatch: Dispatch<AnalyseState>, legacyClient: WebPortalLegacyClientInternal) {
    const point = state.core.peaksJs.waveform.activePoint;
    const eventType = state.core.availableEventTypes.find(x => x.id === point.eventTypeId);

    const multiDayOffset = state.core.peaksJs.options.multiDayOffset || 0;
    point.timestamp += multiDayOffset; 

    // If there is already a point of the SAME TYPE, then editing the point to arrive at this position is not a valid edit.
    if (state.core.peaksJs.pointOrSegmentExists(point, !eventType.period)) {
        toast.error("A Marker Of Type " + eventType.name + " Already Exists At This Point.");
        return;
    }

    removeEventFetch(state, point, dispatch, legacyClient).then((oldEvent) => {
        point.editMode = false;
        addEventFetch(state, point, dispatch, legacyClient).then((response) => {

            if (response?.Exception) {
                point.timestamp = oldEvent.RawStart;
                addEventFetch(state, point, dispatch, legacyClient);
            }

            // The edit is now complete, can now switch peaks back to non-edit mode.
            dispatch(Actions.ClosePeaksEditModeAction.create());
        });
    });
}

function finishMovePeriodEvent(state: AnalyseState, dispatch: Dispatch<AnalyseState>, coughMarshalClient: CoughMarshalClient, legacyClient: WebPortalLegacyClientInternal) {
    const period = state.core.peaksJs.waveform.activeSegment;

    const multiDayOffset = state.core.peaksJs.options.multiDayOffset || 0;
    period.startTime += multiDayOffset;
    period.endTime += multiDayOffset; 

    return removeEventFetch(state, period, dispatch, legacyClient).then((oldEvent) => {
            period.editMode = false;
        return addEventFetch(state, period, dispatch, legacyClient).then((response) => {
            if (response?.Exception) {
                period.startTime = oldEvent.RawStart;
                period.endTime = oldEvent.RawEnd;
                addEventFetch(state, period, dispatch, legacyClient);
            }
        });
        })
        .then(() => dispatch(Actions.DeselectEditingEventsAction.create()));
}

function addEventFetch(state: AnalyseState, cliEvent: PeaksPoint | PeaksPeriod, dispatch: Dispatch<AnalyseState>, legacyClient: WebPortalLegacyClientInternal) {
    const eventType = state.core.availableEventTypes.find(x => x.id === cliEvent.eventTypeId);

    if (eventType.period) {
        toast.info(
            "Marking " +
            state.core.peaksJs.segments.getSegmentEventTypeLabel(cliEvent as PeaksPeriod) +
            "."
        );
    } else {
        toast.info(
            "Marking " +
            state.core.peaksJs.points.getPointEventTypeLabel(cliEvent as PeaksPoint) +
            "."
        );
    }

    // The eventTypeId is used as the user may have selected another event in the dropdown whiled editing another type of point.
    // It cannot be assumed that when an event is added/removed that the event type is the type currently chosen.
    return handleAddEventFetch(
        cliEvent,
        mapToServerEvent(
            cliEvent,
            state.analysisSet.primaryAnalystSetId,
            cliEvent.eventTypeId,
            state.core.selectedWaveform
        ),
        eventType,
        state,
        dispatch,
        legacyClient,
    );
}

const handleAddEventFetch = (
    cliEvent: PeaksPoint | PeaksPeriod,
    serverEvent: ServerEvent,
    eventType: StudyCoughEventType,
    state: AnalyseState,
    dispatch: Dispatch<AnalyseState>,
    legacyClient: WebPortalLegacyClientInternal,
) => {
    try {
        const eventMapping = MapPeriod(state.core.positions, serverEvent.RawStart, serverEvent.RawEnd, serverEvent.ProcessedStart, serverEvent.ProcessedEnd, serverEvent.SensorProcessedStart, serverEvent.SensorProcessedEnd, serverEvent.vtaCt1Start, serverEvent.vtaCt1End, state.core.selectedSegment);
        return legacyClient.putCoughAnalysisSetEvent({
            AnalysisSetId: serverEvent.AnalysisSetId,
            EventTypeId: serverEvent.EventTypeId,
            RawStart: eventMapping.rawStart,
            RawEnd: eventMapping.rawEnd,
            ProcessedStart: eventMapping.processedStart,
            ProcessedEnd: eventMapping.processedEnd,
            SensorProcessedStart: eventMapping.sensorProcessedStart,
            SensorProcessedEnd: eventMapping.sensorProcessedEnd,
            VtaCt1Start: eventMapping.vtaCt1Start,
            VtaCt1End: eventMapping.vtaCt1End,
            SegmentOffset: ((state.core.selectedSegment * 24) - 24) * 60 * 60
        })
            .then(json => {
                cliEvent = {
                    ...cliEvent,
                    id: json.Id
                };
                if (eventType.period) {
                    state.core.peaksJs.segments.add(cliEvent as PeaksPeriod);
                    toast.success(
                        state.core.peaksJs.segments.getSegmentEventTypeLabel(cliEvent as PeaksPeriod) +
                        " marked."
                    );

                    dispatch(Actions.PointOrPeriodAddedEvent.create(state.core.peaksJs.segments.getSegments().find(s => s.id === cliEvent.id)));

                } else {
                    const coughPeakType = state.core.availableEventTypes.find(x => x.refEventTypeId === CoughEventTypeEnum.CoughPeak);
                    const matchingPoints = state.core.peaksJs.points.getPoints().filter(p => p.eventTypeId === coughPeakType.id && p.layerPosition === 0 && Math.abs((state.core.rawTimestampPoints.get(p.id) * 1000) - (json.RawStart * 1000)) <= state.core.eventDuplicateThreshold);
                    const anyMatch = json.EventTypeId === coughPeakType.id ? matchingPoints.length > 0 : false;

                    state.core.peaksJs.points.add({ ...cliEvent, withinDuplicateThreshold: anyMatch } as PeaksPoint);

                    state.core.rawTimestampPoints.set(json.Id, json.RawStart);

                    dispatch(Actions.PointOrPeriodAddedEvent.create(state.core.peaksJs.points.getPoints().find(s => s.id === cliEvent.id)));
                    
                    if (anyMatch) {
                        for (const matchPoint of matchingPoints) {
                            state.core.peaksJs.points.remove(matchPoint);
                            matchPoint.withinDuplicateThreshold = true;
                            state.core.peaksJs.points.add(matchPoint);
                        }

                        const toastId = toast.warn((t) => WarningToast({
                            closeToast: t.closeToast,
                            data: t.data, toastProps: t.toastProps,
                            eventTimeText: state.core.peaksJs.points.getPointEventTypeLabel(cliEvent as PeaksPoint),
                            point: state.core.peaksJs.points.getPoints().find(x => x.id === cliEvent.id)
                        }), {
                            toastId: cliEvent.id,
                            autoClose: false,
                            closeOnClick: false,
                            draggable: false,
                            closeButton: false,

                            data: {
                                onUndo: () => dispatch(
                                    Actions.RemovePointEventAction.create(
                                        legacyClient,
                                        cliEvent.id
                                    )),
                                onJumpTo: (timepoint) => dispatch(
                                    Actions.JumpToTimePointAction.create(
                                        timepoint
                                    ))
                            }
                        });
                    } else {
                        toast.success(
                            state.core.peaksJs.points.getPointEventTypeLabel(cliEvent as PeaksPoint) +
                            " marked."
                        );
                    }
                }
            })
            .catch(error => { toast.error(`Failed marking ${eventType.name}. ${error.Message}`); return error; })
    }
    catch (error) {
        toast.error(error.message);
        return error;
    }
}

function removeEventFetch(
    state: AnalyseState,
    event: PeaksPoint | PeaksPeriod,
    dispatch: Dispatch<AnalyseState>,
    legacyClient: WebPortalLegacyClientInternal
) {
    const removeEvent = {
        Id: event.id
    };

    const eventTypes = state.core.availableEventTypes;
    const eventType = eventTypes.find(x => x.id === event.eventTypeId);

    toast.dismiss(event.id);

    return legacyClient.deleteCoughEvent(removeEvent)
    .then(response => response.json())
        .then(json => {
            // Using the remove API in peaks to remove the point/period.
            // Peaks does the work of determining if the event is a point or period.
            state.core.peaksJs.remove(event);

            dispatch(Actions.PointOrPeriodRemovedEvent.create(event.isPoint, event.id));

            return json;
        })
        .catch(error => toast.error(`Failed to remove ${eventType.name}.`));
}

function jumpToEnd(state: AnalyseState) {
    state.core.peaksJs.player.seekBySeconds(state.core.audioLength - 0.001);
}
